import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { generateShareText } from '../../utils/generateShareText';
import { fetchPuzzleStatus } from '../../utils/api';
import { generateDailyPuzzle, getYesterdayDateString } from '../../utils/puzzleGenerator';
import MiniGrid from '../../components/MiniGrid';
import { scoreToDotColor } from '../../utils/scoreColors';

const BUCKET_LABELS = ['0-10', '10-20', '20-30', '30-40', '40-50', '50-60', '60-70', '70-80', '80-90'];

export default function LeaderboardView({ puzzle, totalScore, username, grid, retryCount, protestResults }) {
  const { fetchLeaderboard } = useLeaderboard(puzzle);
  const [data, setData] = useState(null);
  const [yesterdayData, setYesterdayData] = useState(null);
  const [yesterdayDefs, setYesterdayDefs] = useState(null);
  const [copied, setCopied] = useState(false);

  const yesterdayDate = useMemo(() => getYesterdayDateString(), []);
  const yesterdayPuzzle = useMemo(() => generateDailyPuzzle(yesterdayDate), [yesterdayDate]);

  const handleShare = useCallback(() => {
    if (!grid) return;
    const text = generateShareText(grid, totalScore, puzzle.puzzleNumber, retryCount, protestResults);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [grid, totalScore, puzzle.puzzleNumber, retryCount, protestResults]);

  useEffect(() => {
    fetchLeaderboard().then((d) => setData(d));
    fetchLeaderboard(yesterdayDate).then((d) => setYesterdayData(d));
    fetchPuzzleStatus(yesterdayDate).then((s) => setYesterdayDefs(s?.definitions || null));
  }, [fetchLeaderboard, yesterdayDate]);

  const userInTop5 = data?.topScores?.some(
    (s) => s.score === totalScore && s.name === username
  );

  const userBucket = Math.min(Math.floor(totalScore / 10), 8);

  const averageCells = data?.cellAverages?.map((score) => ({ word: '', score }));

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white border-2 border-ink p-4 sm:p-8 max-w-2xl w-full">
        <h2 className="text-2xl sm:text-3xl font-serif font-black text-center text-ink mb-1">
          {username || 'Anonymous'}: {totalScore.toFixed(1)}
        </h2>

        {grid && (
          <button
            onClick={handleShare}
            className="mx-auto block mt-2 mb-4 px-6 py-2 bg-white text-ink font-serif font-bold border-2 border-ink hover:bg-gray-50 transition-colors text-sm"
          >
            {copied ? 'Copied!' : 'Share'}
          </button>
        )}

        {userInTop5 && (
          <div className="border-2 border-ink p-3 mb-6 text-center">
            <p className="text-sm font-serif font-bold text-ink">
              You made the top 5 today!
            </p>
          </div>
        )}

        {data ? (
          <div className="space-y-6">
            {/* Top 5 */}
            {data.topScores && data.topScores.length > 0 && (
              <div>
                <h3 className="text-lg font-serif font-bold text-ink mb-3">Top 5 Today</h3>
                <div className="space-y-1">
                  {data.topScores.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex justify-between items-center px-3 py-2 font-body text-sm ${
                        entry.name === username && entry.score === totalScore
                          ? 'border-2 border-ink font-bold'
                          : 'border border-ink-faint'
                      }`}
                    >
                      <span className="text-ink-light font-body">#{entry.rank}</span>
                      <span className="text-ink font-body font-semibold">{entry.name}</span>
                      <span className="text-ink font-body font-bold">{entry.score.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Today's Average Grid */}
            {averageCells && (
              <div>
                <h3 className="text-lg font-serif font-bold text-ink mb-3">Today's Averages</h3>
                <MiniGrid cells={averageCells} puzzle={puzzle} hideWords definitions={puzzle.definitions} />
              </div>
            )}

            {/* Score Distribution */}
            {data.scoreDistribution && (
              <div>
                <h3 className="text-lg font-serif font-bold text-ink mb-3">Score Distribution</h3>
                <div className="space-y-1">
                  {data.scoreDistribution.map((count, i) => {
                    const maxCount = Math.max(...data.scoreDistribution, 1);
                    const widthPct = (count / maxCount) * 100;
                    const isUserBucket = i === userBucket;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs font-body">
                        <span className="w-10 text-right text-ink-light">{BUCKET_LABELS[i]}</span>
                        <div className="flex-1 h-5 bg-gray-100 relative">
                          <div
                            className="h-full"
                            style={{
                              width: `${widthPct}%`,
                              backgroundColor: scoreToDotColor((i * 10 + 5) / 9),
                              opacity: isUserBucket ? 1 : 0.6,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Yesterday's Best Words */}
            {yesterdayData && yesterdayData.bestWordsPerCell && (
              <div>
                <h3 className="text-lg font-serif font-bold text-ink mb-3">
                  Yesterday's Best Words
                  <span className="text-sm font-normal text-ink-light ml-2">
                    #{yesterdayPuzzle.puzzleNumber}
                  </span>
                </h3>
                <MiniGrid cells={yesterdayData.bestWordsPerCell} puzzle={yesterdayPuzzle} definitions={yesterdayDefs} />
              </div>
            )}

            <p className="text-xs text-ink-faint text-center font-body">
              {data.totalPlayers} player{data.totalPlayers !== 1 ? 's' : ''} today
              {data.averageScore ? ` \u2022 Average: ${data.averageScore.toFixed(1)}` : ''}
            </p>
          </div>
        ) : (
          <p className="text-center text-ink-faint font-body">Loading leaderboard...</p>
        )}

        {grid && (
          <button
            onClick={handleShare}
            className="w-full mt-6 py-3 bg-white text-ink font-serif font-bold border-2 border-ink hover:bg-gray-50 transition-colors"
          >
            {copied ? 'Copied!' : 'Share'}
          </button>
        )}
      </div>
    </div>
  );
}
